/**
 * Streak Calculation Utilities
 * Handles streak logic for habit completion tracking
 */
import { isToday, isYesterday, isSameDay, parseISO, startOfDay } from 'date-fns';

/**
 * Parses a date value into a JavaScript Date object
 * Handles Firestore Timestamps, Date objects, and ISO strings
 * @param {Date|Object|string|null} dateValue - The date to parse
 * @returns {Date|null} - Parsed Date object or null
 */
export const parseDate = (dateValue) => {
  if (!dateValue) return null;
  
  // Handle Firestore Timestamp
  if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
    return dateValue.toDate();
  }
  
  // Handle Date object
  if (dateValue instanceof Date) {
    return dateValue;
  }
  
  // Handle ISO string
  if (typeof dateValue === 'string') {
    return parseISO(dateValue);
  }
  
  // Handle Firestore Timestamp with seconds
  if (dateValue?.seconds) {
    return new Date(dateValue.seconds * 1000);
  }
  
  return null;
};

/**
 * Calculates the new streak values when a habit is completed (checked)
 * @param {Object} habit - The habit object with streak data
 * @param {Date} completionDate - The date of completion (defaults to now)
 * @returns {Object} - Updated streak values { currentStreak, longestStreak, lastCompletedDate }
 */
export const calculateStreakOnComplete = (habit, completionDate = new Date()) => {
  const {
    currentStreak = 0,
    longestStreak = 0,
    lastCompletedDate
  } = habit;

  const lastDate = parseDate(lastCompletedDate);
  const today = startOfDay(completionDate);
  let newCurrentStreak = currentStreak;
  let newLongestStreak = longestStreak;

  if (lastDate) {
    const lastDateNormalized = startOfDay(lastDate);

    // If already completed today, do nothing (prevent double counting)
    if (isSameDay(lastDateNormalized, today)) {
      return {
        currentStreak,
        longestStreak,
        lastCompletedDate: lastDate.toISOString(),
        streakChanged: false
      };
    }

    // If last completion was yesterday, increment streak
    if (isYesterday(lastDateNormalized)) {
      newCurrentStreak = currentStreak + 1;
    } else {
      // Streak is broken - reset to 1
      newCurrentStreak = 1;
    }
  } else {
    // First time completing this habit - start streak at 1
    newCurrentStreak = 1;
  }

  // Update longest streak if current exceeds it
  if (newCurrentStreak > longestStreak) {
    newLongestStreak = newCurrentStreak;
  }

  return {
    currentStreak: newCurrentStreak,
    longestStreak: newLongestStreak,
    lastCompletedDate: completionDate.toISOString(),
    streakChanged: true
  };
};

/**
 * Calculates the new streak values when a habit is uncompleted (unchecked)
 * @param {Object} habit - The habit object with streak data
 * @param {Date} uncompleteDate - The date being uncompleted (defaults to now)
 * @returns {Object} - Updated streak values { currentStreak, longestStreak, lastCompletedDate }
 */
export const calculateStreakOnUncomplete = (habit, uncompleteDate = new Date()) => {
  const {
    currentStreak = 0,
    longestStreak = 0,
    lastCompletedDate
  } = habit;

  const lastDate = parseDate(lastCompletedDate);
  
  // Only decrement if the uncomplete is for today's completion
  if (lastDate && isToday(startOfDay(lastDate))) {
    const newCurrentStreak = Math.max(0, currentStreak - 1);
    
    // If there was a previous streak (decrementing from > 1), set lastCompletedDate to yesterday
    // This restores the streak state as if today was never completed
    let newLastCompletedDate = null;
    if (newCurrentStreak > 0) {
      // Set to yesterday so the streak remains active
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      newLastCompletedDate = yesterday.toISOString();
    }
    
    return {
      currentStreak: newCurrentStreak,
      longestStreak, // Don't change longest streak on uncomplete
      lastCompletedDate: newLastCompletedDate,
      streakChanged: true
    };
  }

  // If uncompleting a non-today completion, don't change streak
  return {
    currentStreak,
    longestStreak,
    lastCompletedDate: lastDate?.toISOString() || null,
    streakChanged: false
  };
};

/**
 * Gets the display streak value for a habit
 * If the streak is broken (last completion older than yesterday), returns 0
 * @param {Object} habit - The habit object with streak data
 * @returns {number} - The streak value to display
 */
export const getDisplayStreak = (habit) => {
  const { currentStreak = 0, lastCompletedDate } = habit;
  
  if (!lastCompletedDate || currentStreak === 0) {
    return 0;
  }

  const lastDate = parseDate(lastCompletedDate);
  if (!lastDate) {
    return 0;
  }

  const lastDateNormalized = startOfDay(lastDate);
  const today = startOfDay(new Date());

  // If completed today, show current streak
  if (isSameDay(lastDateNormalized, today)) {
    return currentStreak;
  }

  // If completed yesterday, streak is still active
  if (isYesterday(lastDateNormalized)) {
    return currentStreak;
  }

  // Streak is broken - display as 0
  return 0;
};

/**
 * Checks if a streak is considered "active" (not broken)
 * @param {Object} habit - The habit object with streak data
 * @returns {boolean} - Whether the streak is active
 */
export const isStreakActive = (habit) => {
  return getDisplayStreak(habit) > 0;
};

/**
 * Gets streak styling based on streak value
 * @param {number} streak - The current streak value
 * @param {boolean} isDark - Whether dark mode is enabled
 * @returns {Object} - Styling object { bgColor, textColor, borderColor, isActive }
 */
export const getStreakStyle = (streak, isDark = true) => {
  // Mirrors ink / muted tokens in theme.js (rgba so callers can vary alpha)
  if (streak >= 3) {
    const rgb = isDark ? '232, 231, 225' : '20, 20, 18';
    return {
      bgColor: `rgba(${rgb}, 0.1)`,
      textColor: `rgb(${rgb})`,
      borderColor: `rgba(${rgb}, 0.25)`,
      isActive: true
    };
  }

  return {
    bgColor: isDark ? 'rgb(26, 28, 27)' : 'rgb(235, 232, 225)',
    textColor: isDark ? 'rgb(143, 144, 139)' : 'rgb(107, 103, 95)',
    borderColor: 'transparent',
    isActive: streak > 0
  };
};

/**
 * Recalculates streak entirely from the completions map.
 * Used when toggling any date (past or present) to properly reconnect broken streaks.
 * Walks backwards from today through consecutive completed days.
 * Also scans all completions to find the true longest streak.
 *
 * @param {string} habitId - The habit ID
 * @param {Object} completions - Full completions map { habitId: { 'YYYY-MM-DD': boolean } }
 * @param {Object} habit - The habit object (for preserving historical longestStreak)
 * @returns {Object} - { currentStreak, longestStreak, lastCompletedDate }
 */
export const recalculateStreakFromCompletions = (habitId, completions, habit = {}) => {
  const habitCompletions = completions[habitId] || {};

  // Collect all completed date strings (where value is truthy), sorted ascending
  const completedDates = Object.entries(habitCompletions)
    .filter(([, completed]) => completed)
    .map(([dateStr]) => dateStr)
    .sort();

  if (completedDates.length === 0) {
    return {
      currentStreak: 0,
      longestStreak: habit.longestStreak || 0,
      lastCompletedDate: null,
    };
  }

  // Helper: format a Date as YYYY-MM-DD (matching the app's formatDate)
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  // --- Current streak: walk backwards from today ---
  const today = startOfDay(new Date());
  let currentStreak = 0;
  let checkDate = new Date(today);

  // Check today first
  if (habitCompletions[fmt(checkDate)]) {
    // Today is completed — count backwards from today
    while (habitCompletions[fmt(checkDate)]) {
      currentStreak++;
      checkDate.setDate(checkDate.getDate() - 1);
    }
  } else {
    // Today not completed — check if yesterday starts a chain (streak still "active")
    checkDate.setDate(checkDate.getDate() - 1);
    while (habitCompletions[fmt(checkDate)]) {
      currentStreak++;
      checkDate.setDate(checkDate.getDate() - 1);
    }
  }

  // --- Longest streak: scan all completions for the longest consecutive run ---
  let longestStreak = 0;
  let runLength = 1;

  for (let i = 1; i < completedDates.length; i++) {
    const prev = parseISO(completedDates[i - 1]);
    const curr = parseISO(completedDates[i]);
    const diffMs = curr.getTime() - prev.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      runLength++;
    } else {
      longestStreak = Math.max(longestStreak, runLength);
      runLength = 1;
    }
  }
  longestStreak = Math.max(longestStreak, runLength);

  // Preserve historical longest if it was higher (e.g. from before we tracked completions)
  longestStreak = Math.max(longestStreak, habit.longestStreak || 0);
  // Current streak can also set a new record
  longestStreak = Math.max(longestStreak, currentStreak);

  // Last completed date is the most recent completion
  const lastCompletedDate = parseISO(completedDates[completedDates.length - 1]).toISOString();

  return {
    currentStreak,
    longestStreak,
    lastCompletedDate,
  };
};

/**
 * Initializes streak fields for a habit if they don't exist
 * @param {Object} habit - The habit object
 * @returns {Object} - The habit with initialized streak fields
 */
export const initializeStreakFields = (habit) => {
  return {
    ...habit,
    currentStreak: habit.currentStreak ?? 0,
    longestStreak: habit.longestStreak ?? 0,
    lastCompletedDate: habit.lastCompletedDate ?? null
  };
};
